///
/// EarthBrowser
///
/// Created by Matt Giger on 12/31/2025
/// Copyright (c) 2025 Geodata Labs
///

import { Database, type Priority } from "@/earthbrowser/database/Database"
import type { LocationInfo } from "@shared-types/location-info"
import { latLngToCell } from "h3-js"
import { CITY_RANK_DEFAULT, CITY_RANK_LEVELS, type CityImportance } from "./city-types"

const cBatchSize = 500

/** Tier order for combining rank-based and population-based importance */
const cImportanceOrder: CityImportance[] = ['town', 'small', 'medium', 'major', 'capital']

/**
 * Rank algorithm version, stored in locations_meta after computation.
 * Bump this to force a rank recompute on existing installs.
 */
const cRankVersion = 2

/**
 * Per-level suppression radius in km (index = rank level).
 * A city is suppressed at a level if a larger city lies within this distance.
 * Roughly 1/3 of each level's nominal grid spacing (20000km / 2^z), so
 * labels at that level can render without crowding each other.
 */
const cRankSuppressionKm = [6700, 3350, 1675, 840, 420, 210, 105, 52]

/**
 * Per-level minimum population (index = rank level).
 * Prevents small towns from claiming coarse ranks just because their
 * surrounding area is empty (e.g. Aberdeen WA at 17k owning an otherwise
 * empty coastal cell must not outrank Portland at 653k).
 */
const cRankMinPop = [5_000_000, 3_000_000, 2_000_000, 1_000_000, 500_000, 100_000, 50_000, 10_000]

export type LocationRow = {
	geoid: number
	name: string
	country: string
	admin1: string
	admin2: string
	tz: string
	pop: number
	lon: number
	lat: number
	alt: number
	code: string
	geom: string
	h3_r3: string
}

/**
 * Row returned from R-Tree bbox query with computed importance
 */
export type CityQueryRow = {
	geoid: number
	name: string
	country: string
	admin1: string
	admin2: string
	tz: string
	pop: number
	lon: number
	lat: number
	alt: number
	code: string
	importance: CityImportance
	rank: number
}

export class LocationsTable {
	public static async create(db: Database): Promise<void> {
		await db.execMany([
			{
				sql: `create table if not exists locations (
					geoid integer primary key,
					name text not null,
					country text not null,
					admin1 text,
					admin2 text,
					pop integer not null default 0,
					lon real not null,
					lat real not null,
					alt integer not null default 0,
					code text,
					tz text,
					geom blob,
					h3_r3 text,
					rank integer not null default ${CITY_RANK_DEFAULT}
				)`
			},
			{ sql: "create index if not exists idx_locations_name on locations (name)" },
			{ sql: "create index if not exists idx_locations_pop on locations (pop desc)" },
			{ sql: "create index if not exists idx_locations_country_admin on locations (country, admin1)" },
			{ sql: "create index if not exists idx_locations_h3_r3 on locations (h3_r3)" },

			// Metadata for the locations dataset (e.g. rank algorithm version)
			{ sql: "create table if not exists locations_meta (key text primary key, value text)" },

			// Spatial index (R*Tree) + triggers to keep it in sync
			{
				sql: `create virtual table if not exists locations_idx using rtree(
					id,
					min_x, max_x,
					min_y, max_y
				)`
			},
			{
				sql: `create trigger if not exists locations_idx_insert
				after insert on locations
				begin
					insert into locations_idx values (
						new.rowid,
						ST_XMin(new.geom),
						ST_XMax(new.geom),
						ST_YMin(new.geom),
						ST_YMax(new.geom)
					);
				end`
			},
			// Recreate the update trigger scoped to geom changes only, so that
			// rank-only updates (computed after ingest) don't churn the R*Tree.
			// Drop first: existing installs have the old unscoped trigger.
			{ sql: "drop trigger if exists locations_idx_update" },
			{
				sql: `create trigger locations_idx_update
				after update of geom on locations
				begin
					delete from locations_idx where id = old.rowid;
					insert into locations_idx values (
						new.rowid,
						ST_XMin(new.geom),
						ST_XMax(new.geom),
						ST_YMin(new.geom),
						ST_YMax(new.geom)
					);
				end`
			},
			{
				sql: `create trigger if not exists locations_idx_delete
				after delete on locations
				begin
					delete from locations_idx where id = old.rowid;
				end`
			},
		])

		// Migrate existing installs: add the rank column if it's missing
		// (create table if not exists won't alter an existing table)
		const columns = await db.exec("pragma table_info(locations)") as Array<{ name: string }>
		if (Array.isArray(columns) && !columns.some(c => c.name === "rank")) {
			await db.exec(`alter table locations add column rank integer not null default ${CITY_RANK_DEFAULT}`)
		}
		await db.exec("create index if not exists idx_locations_rank on locations (rank, pop desc)")
	}

	/**
	 * Bulk ingest of the cities checkpoint (~280k rows on a fresh install,
	 * each firing the R*Tree trigger). Queued at low priority so the batches
	 * never starve the reads that put visible layers on screen: the DB queue
	 * is strictly priority-ordered, and at the default priority a stream of
	 * these batches would delay a lower-priority read until the whole
	 * ingest finished
	 */
	public static async insert(locations: LocationInfo[]): Promise<void> {
		// Use bulkExec with individual statements since bulkInsert doesn't handle SQL functions properly
		for (let i = 0; i < locations.length; i += cBatchSize) {
			const batch = locations.slice(i, i + cBatchSize)
			const commands = batch.map(loc => ({
				sql: `insert into locations (geoid, name, country, admin1, admin2, tz, pop, lon, lat, alt, code, geom, h3_r3)
					values (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ST_SetSRID(ST_MakePoint(?, ?), 4326), ?)
					on conflict(geoid) do update set
						name = excluded.name,
						country = excluded.country,
						admin1 = excluded.admin1,
						admin2 = excluded.admin2,
						tz = excluded.tz,
						pop = excluded.pop,
						lon = excluded.lon,
						lat = excluded.lat,
						alt = excluded.alt,
						code = excluded.code,
						geom = excluded.geom,
						h3_r3 = excluded.h3_r3`,
				params: [
					loc.geoid,
					loc.name,
					loc.country,
					loc.admin1,
					loc.admin2,
					loc.tz,
					loc.pop,
					loc.lon,
					loc.lat,
					loc.alt,
					loc.code,
					loc.lon,
					loc.lat,
					latLngToCell(loc.lat, loc.lon, 3),
				]
			}))
			await Database.db.bulkExec(commands, "low")
		}
	}

	/**
	 * Query cities within a bounding box using R*Tree spatial index
	 * Returns cities ordered by spatial-dominance rank (then population),
	 * so truncation by limit preserves geographic coverage.
	 *
	 * @param minLon - Minimum longitude (degrees)
	 * @param minLat - Minimum latitude (degrees)
	 * @param maxLon - Maximum longitude (degrees)
	 * @param maxLat - Maximum latitude (degrees)
	 * @param limit - Maximum number of cities to return
	 * @param maxRank - Maximum label rank to include (density control)
	 */
	public static async queryByBBox(
		minLon: number,
		minLat: number,
		maxLon: number,
		maxLat: number,
		limit: number = 1000,
		maxRank: number = CITY_RANK_DEFAULT,
		priority: Priority = "medium"
	): Promise<CityQueryRow[]> {
		// Query using R*Tree index for spatial filtering
		const statement = `
			SELECT l.geoid, l.name, l.country, l.admin1, l.admin2, l.tz, l.pop, l.lon, l.lat, l.alt, l.code, l.rank
			FROM locations l
			INNER JOIN locations_idx idx ON l.rowid = idx.id
			WHERE idx.min_x <= ? AND idx.max_x >= ?
			  AND idx.min_y <= ? AND idx.max_y >= ?
			  AND l.rank <= ?
			ORDER BY l.rank ASC, l.pop DESC
			LIMIT ?
		`

		const rows = await Database.db.exec(statement, [
			maxLon,  // idx.min_x <= maxLon
			minLon,  // idx.max_x >= minLon
			maxLat,  // idx.min_y <= maxLat
			minLat,  // idx.max_y >= minLat
			maxRank,
			limit
		], priority) as Array<{
			geoid: number
			name: string
			country: string
			admin1: string
			admin2: string
			tz: string
			pop: number
			lon: number
			lat: number
			alt: number
			code: string
			rank: number
		}>

		// Add computed importance to each row
		return rows.map(row => ({
			...row,
			importance: LocationsTable.computeImportance(row.pop, row.rank)
		}))
	}

	/**
	 * Query places nearest to a point, ordered by distance - for reverse
	 * geocoding (naming dropped pins), not label display. Unlike
	 * queryByBBox there is no rank/population ordering: a tiny town right
	 * at the point must win over a big city 40 km away, so truncation by
	 * limit must never drop the closest rows. Distance is approximated in
	 * SQL as squared degrees with the longitude axis scaled by
	 * cos(latitude), which preserves ordering at lookup scales.
	 *
	 * @param lat - Latitude of the query point (degrees)
	 * @param lon - Longitude of the query point (degrees)
	 * @param radiusDeg - Half-width of the R*Tree prefilter box (degrees)
	 * @param limit - Maximum number of places to return
	 */
	public static async queryNearest(
		lat: number,
		lon: number,
		radiusDeg: number,
		limit: number = 5
	): Promise<CityQueryRow[]> {
		const lonScaleSq = Math.cos(lat * Math.PI / 180) ** 2
		const statement = `
			SELECT l.geoid, l.name, l.country, l.admin1, l.admin2, l.tz, l.pop, l.lon, l.lat, l.alt, l.code, l.rank
			FROM locations l
			INNER JOIN locations_idx idx ON l.rowid = idx.id
			WHERE idx.min_x <= ? AND idx.max_x >= ?
			  AND idx.min_y <= ? AND idx.max_y >= ?
			ORDER BY (l.lat - ?) * (l.lat - ?) + (l.lon - ?) * (l.lon - ?) * ?
			LIMIT ?
		`

		const rows = await Database.db.exec(statement, [
			lon + radiusDeg,  // idx.min_x <= maxLon
			lon - radiusDeg,  // idx.max_x >= minLon
			lat + radiusDeg,  // idx.min_y <= maxLat
			lat - radiusDeg,  // idx.max_y >= minLat
			lat,
			lat,
			lon,
			lon,
			lonScaleSq,
			limit
		]) as Array<{
			geoid: number
			name: string
			country: string
			admin1: string
			admin2: string
			tz: string
			pop: number
			lon: number
			lat: number
			alt: number
			code: string
			rank: number
		}>

		return rows.map(row => ({
			...row,
			importance: LocationsTable.computeImportance(row.pop, row.rank)
		}))
	}

	/**
	 * Query top cities globally by spatial-dominance rank
	 * Used when camera is at high altitude (global view).
	 * Rank ordering guarantees every region's dominant cities are included
	 * before any region gets additional lower-rank cities.
	 *
	 * @param limit - Maximum number of cities to return
	 * @param maxRank - Maximum label rank to include (density control)
	 */
	public static async queryTopCities(
		limit: number = 1000,
		maxRank: number = CITY_RANK_DEFAULT
	): Promise<CityQueryRow[]> {
		const statement = `
			SELECT geoid, name, country, admin1, admin2, tz, pop, lon, lat, alt, code, rank
			FROM locations
			WHERE rank <= ?
			ORDER BY rank ASC, pop DESC
			LIMIT ?
		`

		const rows = await Database.db.exec(statement, [
			maxRank,
			limit
		]) as Array<{
			geoid: number
			name: string
			country: string
			admin1: string
			admin2: string
			tz: string
			pop: number
			lon: number
			lat: number
			alt: number
			code: string
			rank: number
		}>

		return rows.map(row => ({
			...row,
			importance: LocationsTable.computeImportance(row.pop, row.rank)
		}))
	}

	/**
	 * Query a single city by geoid
	 * Used for on-demand loading when a city isn't in the current viewport
	 *
	 * @param geoid - The GeoNames ID of the city
	 * @returns The city data or undefined if not found
	 */
	public static async queryByGeoid(geoid: number): Promise<CityQueryRow | undefined> {
		const statement = `
			SELECT geoid, name, country, admin1, admin2, tz, pop, lon, lat, alt, code, rank
			FROM locations
			WHERE geoid = ?
			LIMIT 1
		`

		const rows = await Database.db.exec(statement, [geoid]) as Array<{
			geoid: number
			name: string
			country: string
			admin1: string
			admin2: string
			tz: string
			pop: number
			lon: number
			lat: number
			alt: number
			code: string
			rank: number
		}>

		if (rows.length === 0) return undefined

		const row = rows[0]
		return {
			...row,
			importance: LocationsTable.computeImportance(row.pop, row.rank)
		}
	}

	public static async getCount(): Promise<number> {
		const rows = await Database.db.exec(
			`select count(*) as count from locations`,
			[],
			"low"
		) as Array<{ count: number }>
		return Number(rows[0]?.count ?? 0)
	}

	/**
	 * Compute city importance tier from spatial-dominance rank and population.
	 * Takes the higher of the two tiers: rank captures regional prominence
	 * (Seattle dominates the Pacific Northwest despite a modest city-proper
	 * population), while population keeps genuine megacities prominent even
	 * when a larger neighbor claims their dominance cell.
	 */
	public static computeImportance(population: number, rank: number = CITY_RANK_DEFAULT): CityImportance {
		const rankTier: CityImportance =
			rank <= 1 ? 'capital' :
			rank <= 3 ? 'major' :
			rank <= 5 ? 'medium' :
			rank <= 7 ? 'small' : 'town'

		const popTier: CityImportance =
			population >= 1_000_000 ? 'capital' :
			population >= 500_000 ? 'major' :
			population >= 100_000 ? 'medium' :
			population >= 10_000 ? 'small' : 'town'

		return cImportanceOrder.indexOf(rankTier) >= cImportanceOrder.indexOf(popTier) ? rankTier : popTier
	}

	// ==================== Spatial-Dominance Rank ====================

	/**
	 * Check whether label ranks have been computed with the current algorithm.
	 * The version is stored in locations_meta so bumping cRankVersion forces
	 * a recompute on existing installs.
	 */
	public static async ranksComputed(): Promise<boolean> {
		const rows = await Database.db.exec(
			`select value from locations_meta where key = 'rank_version'`
		) as Array<{ value: string }>
		return Number(rows[0]?.value ?? 0) === cRankVersion
	}

	/**
	 * Compute and store spatial-dominance label ranks for all cities.
	 *
	 * A city earns rank z (0 = most important) if:
	 * - its population meets that level's floor (cRankMinPop), and
	 * - no larger city lies within that level's suppression radius
	 *   (cRankSuppressionKm)
	 *
	 * Unlike grid-cell dominance, suppression radii have no cell-boundary
	 * artifacts: Portland (653k) is 233km from the larger Seattle, so it
	 * clears the rank-5 radius even though Seattle would win any shared cell,
	 * while Salem (177k, 70km from Portland) is suppressed to a finer rank.
	 * The population floors prevent small towns in empty areas (Aberdeen,
	 * Coos Bay) from claiming coarse ranks. National capitals (PPLC) are
	 * boosted two levels and admin1 capitals (PPLA) one level, so state and
	 * province capitals like Olympia, Salem, and Victoria outrank
	 * similar-sized neighbors.
	 *
	 * Runs client-side after ingest. Cities are processed largest-first with
	 * per-level spatial hashes, so each city only checks already-placed
	 * (larger) cities within its neighborhood. Writes only changed rows.
	 */
	public static async computeAndStoreRanks(): Promise<void> {
		const rows = await Database.db.exec(
			`select geoid, lat, lon, pop, code, rank from locations`
		) as Array<{ geoid: number, lat: number, lon: number, pop: number, code: string | null, rank: number }>

		if (!Array.isArray(rows) || rows.length === 0) return

		const startTime = performance.now()

		// Largest-first: when a city is processed, every already-placed city
		// is larger (ties broken by geoid for determinism across recomputes)
		const cities = rows.filter(r => r.pop > 0 && Number.isFinite(r.lat) && Number.isFinite(r.lon))
		cities.sort((a, b) => b.pop - a.pop || a.geoid - b.geoid)

		// Per-level spatial hash of placed cities. Cell size equals the
		// suppression radius (in latitude degrees), so a 3-row neighborhood
		// always covers the radius vertically.
		const cellSizeDeg = cRankSuppressionKm.map(r => r / 111)
		const colsPerLevel = cellSizeDeg.map(s => Math.max(1, Math.ceil(360 / s)))
		const grids: Array<Map<number, Array<{ lat: number, lon: number }>>> =
			cRankSuppressionKm.map(() => new Map<number, Array<{ lat: number, lon: number }>>())

		const ranks = new Map<number, number>()

		for (const city of cities) {
			// First (coarsest) level whose population floor this city meets
			let zFloor = CITY_RANK_LEVELS
			for (let z = 0; z < CITY_RANK_LEVELS; z++) {
				if (city.pop >= cRankMinPop[z]) {
					zFloor = z
					break
				}
			}

			// Rank = coarsest eligible level where no larger city suppresses it
			let rank = CITY_RANK_DEFAULT
			for (let z = zFloor; z < CITY_RANK_LEVELS; z++) {
				if (!hasNearbyCity(grids[z], colsPerLevel[z], cellSizeDeg[z], cRankSuppressionKm[z], city.lat, city.lon)) {
					rank = z
					break
				}
			}
			if (rank !== CITY_RANK_DEFAULT) {
				ranks.set(city.geoid, rank)
			}

			// Register this city at every level where it could suppress a
			// smaller city (that city must itself meet the level's floor,
			// so a suppressor needs at least that population too)
			for (let z = 0; z < CITY_RANK_LEVELS; z++) {
				if (city.pop >= cRankMinPop[z]) {
					const size = cellSizeDeg[z]
					const cols = colsPerLevel[z]
					const cellRow = Math.floor((city.lat + 90) / size)
					const cellCol = ((Math.floor((city.lon + 180) / size) % cols) + cols) % cols
					const key = cellRow * cGridKeyStride + cellCol
					const bucket = grids[z].get(key)
					if (bucket) {
						bucket.push({ lat: city.lat, lon: city.lon })
					} else {
						grids[z].set(key, [{ lat: city.lat, lon: city.lon }])
					}
				}
			}
		}

		// Capital boosts: national capitals two levels, admin1 (state/province)
		// capitals one level
		for (const city of cities) {
			const boost = city.code === 'PPLC' ? 2 : city.code === 'PPLA' ? 1 : 0
			if (boost > 0) {
				ranks.set(city.geoid, Math.max(0, (ranks.get(city.geoid) ?? CITY_RANK_DEFAULT) - boost))
			}
		}

		// Write back only the rows whose rank changed
		const updates: Array<{ sql: string, params: unknown[] }> = []
		for (const row of rows) {
			const newRank = ranks.get(row.geoid) ?? CITY_RANK_DEFAULT
			if (newRank !== row.rank) {
				updates.push({
					sql: `update locations set rank = ? where geoid = ?`,
					params: [newRank, row.geoid]
				})
			}
		}

		// Background maintenance after ingest; low priority like the ingest
		for (let i = 0; i < updates.length; i += cBatchSize) {
			await Database.db.bulkExec(updates.slice(i, i + cBatchSize), "low")
		}

		// Record the algorithm version so recomputes only happen when it changes
		await Database.db.exec(
			`insert into locations_meta (key, value) values ('rank_version', ?)
				on conflict(key) do update set value = excluded.value`,
			[String(cRankVersion)]
		)

		const elapsed = (performance.now() - startTime).toFixed(0)
		console.log(`[LocationsTable] Computed label ranks for ${cities.length} cities (${updates.length} updated) in ${elapsed}ms`)
	}
}

/** Stride for packing (row, col) grid coordinates into a numeric map key */
const cGridKeyStride = 1_000_000

/**
 * Check whether any placed (larger) city lies within radiusKm of (lat, lon).
 * Searches the 3-row neighborhood around the city's cell, widening the column
 * reach at high latitudes where longitude cells shrink in ground distance.
 */
function hasNearbyCity(
	grid: Map<number, Array<{ lat: number, lon: number }>>,
	cols: number,
	cellSizeDeg: number,
	radiusKm: number,
	lat: number,
	lon: number
): boolean {
	if (grid.size === 0) return false

	const cellRow = Math.floor((lat + 90) / cellSizeDeg)
	const cellCol = Math.floor((lon + 180) / cellSizeDeg)

	// Columns needed each side to cover radiusKm at this latitude
	const cosLat = Math.max(0.15, Math.cos(lat * Math.PI / 180))
	const colReach = Math.min(cols, Math.ceil(1 / cosLat))

	for (let r = cellRow - 1; r <= cellRow + 1; r++) {
		for (let dc = -colReach; dc <= colReach; dc++) {
			const c = (((cellCol + dc) % cols) + cols) % cols
			const bucket = grid.get(r * cGridKeyStride + c)
			if (!bucket) continue
			for (const other of bucket) {
				if (haversineKm(lat, lon, other.lat, other.lon) <= radiusKm) {
					return true
				}
			}
		}
	}
	return false
}

/** Great-circle distance between two lat/lon points in km */
function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
	const rad = Math.PI / 180
	const dLat = (lat2 - lat1) * rad
	const dLon = (lon2 - lon1) * rad
	const a = Math.sin(dLat / 2) ** 2 +
		Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLon / 2) ** 2
	return 12742 * Math.asin(Math.min(1, Math.sqrt(a)))  // 2 * Earth radius (6371km)
}


